import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import Snackbar from "@mui/material/Snackbar";

import AssignmentPickerDialog from "../../components/Directory/AssignmentPickerDialog";
import EffectivePermissionsDrawer from "../../components/Access/EffectivePermissionsDrawer";
import CopyLinkModal from "./CopyLinkModal";
import InviteToProjectDialog from "./InviteToProjectDialog";
import RolePill from "../../components/Access/RolePill";
import { formatRelative, formatIso } from "../../helpers/date";
import { hasPermission } from "../../helpers/selectors";
import { resetUserNda } from "../../helpers/actions";
import {
  getAccountMembers,
  getMe,
  getProjects,
  inviteMember,
  removeMember,
  updateMember,
} from "./api";
import {
  deriveDisplayRole,
  displayName,
  filterMembers,
  initialsOf,
  stringToColor,
} from "./selectors";

/**
 * Members surface for /access/members.
 *
 * Read: GET /accounts/me, /accounts/{id}/members, /k8s-project/ (for
 * the projects-column denominator and per-project grant editing in the
 * Invite dialog).
 * Write: POST/PUT/DELETE /accounts/{id}/members[/{userId}] — folded in
 * from the legacy /members page in SCK-485. Kebab actions Change-role
 * and Remove are live; the Invite button is enabled.
 * RolePill still wears the `preview` badge until Phase 2 guards land
 * (SCK-468/469).
 * lastActive column is a literal "—" (coord API gap; we do not fabricate).
 */
export default function AccessMembers({ currentUser }) {
  const theme = useTheme();

  // Owner/Admin gate. SYSTEM + ACCOUNT_ADMIN see the full member list
  // and get write actions. Everyone else (Developer/Viewer/Billing)
  // still gets the sub-nav shell but the members table is filtered
  // down to a single row (themselves) with a small hint.
  const canViewAllMembers = hasPermission(currentUser, [
    "SYSTEM",
    "ACCOUNT_ADMIN",
  ]);

  const [accountId, setAccountId] = useState(null);
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [syncedAt, setSyncedAt] = useState(null);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuMember, setMenuMember] = useState(null);

  // null | { mode: 'invite' | 'edit', member?: Member }
  const [editing, setEditing] = useState(null);
  // null | Member
  const [removing, setRemoving] = useState(null);
  // SCK-714 — reset-NDA target (same shape as `removing` — the row member).
  const [resettingNda, setResettingNda] = useState(null);
  // null | Member — target of the "View effective permissions" drawer
  const [viewing, setViewing] = useState(null);
  // null | Member — target of the Add-to-group picker fired from the
  // row kebab. Reuses the shared AssignmentPickerDialog so the picker
  // (and its SOC 2 error-toast contract) is the same on both sides.
  const [addingToGroup, setAddingToGroup] = useState(null);

  // SCK-476 — invite-into-a-single-workspace dialog + success toast.
  const [inviteProjectOpen, setInviteProjectOpen] = useState(false);
  const [toast, setToast] = useState(null); // null | { message: string }
  // SCK-619 — copy-once fallback modal for token-invite responses that
  // did not (or could not) send email. Populated by the shared
  // handleInviteResponse helper from both invite entry points.
  const [copyLink, setCopyLink] = useState(null);
  // null | { url, email, expiresOn }

  /**
   * SCK-619 — shared post-mint router used by both MemberFormDialog
   * (account invite) and InviteToProjectDialog (workspace invite).
   * Decides toast wording and whether to open the copy-link modal
   * based on `invitation.deliveryMode`:
   *   - "email"     → SES delivered → "Invitation sent to <email>"
   *   - "copy_only" → SES off / user opted out → copy-link modal +
   *                   "Redemption link ready to copy" toast
   *   - anything else with a `redemptionUrl` → treated as copy_only
   *     (defensive fallback for older coord builds)
   *   - no invitation block → legacy "invited" toast, no copy modal
   */
  const handleInviteResponse = useCallback(
    ({ email, invitation, legacyMessage }) => {
      if (invitation && invitation.deliveryMode === "email") {
        setToast({ message: `Invitation sent to ${email}.` });
        return;
      }
      if (invitation && invitation.redemptionUrl) {
        setCopyLink({
          url: invitation.redemptionUrl,
          email,
          expiresOn: invitation.expires_on || invitation.expiresOn || null,
        });
        setToast({ message: "Redemption link ready to copy." });
        return;
      }
      // Legacy or missing invitation block — coord returned a bare
      // member row or an older shape. Fall back to the pre-SCK-619
      // wording the caller supplied.
      if (legacyMessage) setToast({ message: legacyMessage });
    },
    []
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const self = await getMe();
      setAccountId(self.id);

      if (!canViewAllMembers) {
        // Non-admins can't hit /accounts/{id}/members (coord 403s).
        // Synthesize a single-row list from the caller's identity so
        // the sub-nav shell still renders + they can inspect their
        // own effective permissions.
        const selfMember = {
          userId: currentUser?.id || currentUser?.userId || "me",
          email: currentUser?.email || self.email || "",
          givenName: currentUser?.givenName || "",
          familyName: currentUser?.familyName || "",
          role: "MEMBER",
          canCreateProjects: !!currentUser?.canCreateProjects,
          projects: [],
          grantedOn: null,
        };
        const p = await getProjects().catch(() => []);
        setMembers([selfMember]);
        setProjects(Array.isArray(p) ? p : []);
        setSyncedAt(Date.now());
        return;
      }

      const [m, p] = await Promise.all([
        getAccountMembers(self.id),
        getProjects().catch(() => []),
      ]);
      setMembers(Array.isArray(m) ? m : []);
      setProjects(Array.isArray(p) ? p : []);
      setSyncedAt(Date.now());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [canViewAllMembers, currentUser]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(
    () => filterMembers(query, members),
    [query, members]
  );

  // Sort: newest joiners first (grantedOn DESC), null-safe.
  const sorted = useMemo(() => {
    const withOwnerSurfaced = [...filtered];
    withOwnerSurfaced.sort((a, b) => {
      const ta = a.grantedOn ? Date.parse(a.grantedOn) : 0;
      const tb = b.grantedOn ? Date.parse(b.grantedOn) : 0;
      return tb - ta;
    });
    return withOwnerSurfaced;
  }, [filtered]);

  const totalProjects = projects.length;

  const openMenu = (event, member) => {
    setMenuAnchor(event.currentTarget);
    setMenuMember(member);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuMember(null);
  };

  const copyUserId = () => {
    if (menuMember?.userId) {
      navigator.clipboard?.writeText(menuMember.userId);
    }
    closeMenu();
  };

  const openEdit = () => {
    if (menuMember) setEditing({ mode: "edit", member: menuMember });
    closeMenu();
  };

  const openRemove = () => {
    if (menuMember) setRemoving(menuMember);
    closeMenu();
  };

  // SCK-714 — reset-NDA row action. Available to ACCOUNT_DIR_ADMIN +
  // ACCOUNT_ADMIN (both are the target caller cohorts on the account-
  // scoped view). Coord enforces the same scope server-side; this just
  // hides the action from users who cannot successfully call it.
  const openResetNda = () => {
    if (menuMember) setResettingNda(menuMember);
    closeMenu();
  };

  const openView = () => {
    if (menuMember) setViewing(menuMember);
    closeMenu();
  };

  const openAddToGroup = () => {
    if (menuMember) setAddingToGroup(menuMember);
    closeMenu();
  };

  if (error && error.status === 403) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        You need <strong>Account Admin</strong> to view members. Contact your
        account owner if you need access.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Read-only hint for non-admin callers. Sub-nav is still rendered
          by AccessLayout above; this just explains why the table shows
          only one row. */}
      {!canViewAllMembers && (
        <Alert
          severity="info"
          icon={<LockOutlinedIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          You can only view your own membership. Ask an Account Admin to see
          the full member list.
        </Alert>
      )}

      {/* Toolbar row: search + invite */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or email"
          sx={{ width: { xs: "100%", sm: 320 } }}
          disabled={!canViewAllMembers}
        />
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* SCK-476 — per-project invite. Available to anyone who
              can see projects; backend gates the actual POST (a
              non-admin caller will get a 403 back and the dialog
              renders the "ask a Project/Account Admin" message).
              Verb-object rename (SCK-directory-copy): "Invite to a
              workspace" was ambiguous next to "Invite member"; the
              two CTAs now read as scope-scoped verbs. */}
          <Tooltip
            title="Add someone to a single project inside an account. They see only that project."
            arrow
            enterDelay={400}
          >
            <span>
              <Button
                variant="outlined"
                startIcon={<WorkspacesOutlinedIcon />}
                disabled={!projects.length}
                onClick={() => setInviteProjectOpen(true)}
                sx={{ textTransform: "none" }}
                aria-label="Invite to a project"
              >
                Invite to a project
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title="Add someone to this account. They can access every project in the account per their role."
            arrow
            enterDelay={400}
          >
            <span>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                disabled={!accountId || !canViewAllMembers}
                onClick={() => setEditing({ mode: "invite", member: null })}
                sx={{ textTransform: "none" }}
                aria-label="Invite to account"
              >
                Invite to account
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Error state (non-403) */}
      {error && error.status !== 403 && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              Retry
            </Button>
          }
        >
          Couldn&apos;t load members. {error.message?.slice(0, 200)}
        </Alert>
      )}

      <TableContainer
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          background:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.02)"
              : "rgba(0,0,0,0.01)",
        }}
      >
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="right">Projects</TableCell>
              <TableCell>Last active</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {Array.from({ length: 9 }).map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton
                        variant="rectangular"
                        height={22}
                        sx={{ borderRadius: 0.5 }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!loading && sorted.length === 0 && !error && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Stack
                    spacing={2}
                    alignItems="center"
                    sx={{ py: 6, color: "text.secondary" }}
                  >
                    <ShieldOutlinedIcon sx={{ fontSize: 48, opacity: 0.6 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      No members yet
                    </Typography>
                    <Typography variant="body2">
                      Invite a teammate to collaborate on this account.
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              sorted.map((m) => {
                const role = deriveDisplayRole(m, members, currentUser);
                const projCount = (m.projects || []).length;
                return (
                  <TableRow key={m.userId || m.email} hover>
                    <TableCell sx={{ width: 44, pr: 0 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: 13,
                          fontWeight: 700,
                          bgcolor: stringToColor(m.userId || m.email),
                        }}
                      >
                        {initialsOf(m)}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>
                        {displayName(m)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          maxWidth: 280,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={m.email}
                      >
                        {m.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <RolePill role={role} preview />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={
                          (m.projects || [])
                            .map(
                              (p) =>
                                `${p.projectName || p.projectId}${p.role ? ` (${p.role})` : ""}`
                            )
                            .join("\n") || "No projects"
                        }
                      >
                        <span
                          style={{
                            fontVariantNumeric: "tabular-nums",
                            fontSize: 13,
                          }}
                        >
                          {projCount}
                          {totalProjects
                            ? (
                              <span
                                style={{ color: theme.palette.text.secondary }}
                              >
                                {" "}
                                / {totalProjects}
                              </span>
                            )
                            : null}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Coming soon — needs backend last-sign-in timestamp">
                        <span style={{ color: theme.palette.text.disabled }}>—</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="Password"
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatIso(m.grantedOn) || ""}>
                        <span
                          style={{
                            fontSize: 13,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {formatRelative(m.grantedOn)}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 48 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => openMenu(e, m)}
                        aria-label="Row actions"
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer caption */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 1.5 }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {loading
            ? "Loading members…"
            : `${filtered.length} of ${members.length} members`}
          {syncedAt && (
            <span> · Last synced {formatRelative(new Date(syncedAt).toISOString())}</span>
          )}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={refresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Row-action menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={openView}>View effective permissions</MenuItem>
        <MenuItem onClick={copyUserId}>Copy user ID</MenuItem>
        <Tooltip
          title="Grant this user access via group membership. Roles held by the group apply automatically."
          placement="left"
        >
          <MenuItem
            onClick={openAddToGroup}
            disabled={!accountId || !canViewAllMembers}
          >
            Add to group…
          </MenuItem>
        </Tooltip>
        <MenuItem
          onClick={openEdit}
          disabled={!accountId || !canViewAllMembers}
        >
          Change role…
        </MenuItem>
        <MenuItem
          onClick={openResetNda}
          disabled={!accountId || !canViewAllMembers}
          data-testid={`reset-nda-action-${menuMember?.userId || ""}`}
        >
          Reset NDA acceptance…
        </MenuItem>
        <MenuItem
          onClick={openRemove}
          disabled={!accountId || !canViewAllMembers}
          sx={{ color: "error.main" }}
        >
          Remove from account…
        </MenuItem>
      </Menu>

      {addingToGroup && accountId && (
        <AssignmentPickerDialog
          open
          mode="add_to_group"
          userId={addingToGroup.userId}
          scopeAccountId={accountId}
          onClose={() => setAddingToGroup(null)}
          onSaved={({ count }) => {
            setAddingToGroup(null);
            setToast({
              message: `Added to ${count} group${count === 1 ? "" : "s"}.`,
            });
          }}
        />
      )}

      <EffectivePermissionsDrawer
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        member={viewing}
        memberList={members}
        currentUser={currentUser}
      />

      {editing && accountId && (
        <MemberFormDialog
          mode={editing.mode}
          member={editing.member}
          projects={projects}
          accountId={accountId}
          onClose={() => setEditing(null)}
          onSaved={({ mode: savedMode, email, invitation } = {}) => {
            setEditing(null);
            refresh();
            if (savedMode === "invite") {
              handleInviteResponse({
                email,
                invitation,
                // Legacy fallback wording preserved for older coord
                // shapes that don't yet wrap the response.
                legacyMessage: `Invited ${email}.`,
              });
            }
          }}
        />
      )}
      {removing && accountId && (
        <RemoveConfirmDialog
          member={removing}
          accountId={accountId}
          onClose={() => setRemoving(null)}
          onRemoved={() => {
            setRemoving(null);
            refresh();
          }}
        />
      )}
      {resettingNda && (
        <ResetNdaConfirmDialogAM
          member={resettingNda}
          onClose={() => setResettingNda(null)}
          onDone={(t) => {
            setResettingNda(null);
            setToast(t);
          }}
        />
      )}

      {/* SCK-476 — per-workspace invite dialog. Fires on the caller's
          own project list; posts to that project's owning account. */}
      <InviteToProjectDialog
        open={inviteProjectOpen}
        projects={projects}
        onClose={() => setInviteProjectOpen(false)}
        onInvited={({ email, projectRole, projectName, invitation }) => {
          setInviteProjectOpen(false);
          refresh();
          handleInviteResponse({
            email,
            invitation,
            // Preserve the pre-SCK-619 "invited X to <workspace>"
            // wording when coord returns a legacy body without an
            // invitation block.
            legacyMessage: `Invited ${email} to ${projectName} as ${projectRole
              .replace(/_/g, " ")
              .toLowerCase()}.`,
          });
        }}
      />

      {/* SCK-619 — copy-once fallback for token-invite responses that
          did not send email. Rendered once for both invite flows. */}
      <CopyLinkModal
        open={!!copyLink}
        url={copyLink?.url || ""}
        email={copyLink?.email || ""}
        expiresOn={copyLink?.expiresOn || null}
        onClose={() => setCopyLink(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            severity="success"
            onClose={() => setToast(null)}
            sx={{ width: "100%" }}
            data-testid="invite-success-toast"
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

// ---------------- subcomponents ----------------

/**
 * Invite / edit dialog. In `invite` mode we collect email, account role,
 * canCreateProjects, and optional per-project grants. In `edit` mode
 * email + project grants are fixed (backend only accepts role +
 * canCreateProjects on PUT); to change grants a caller must remove +
 * re-invite for now.
 *
 * Exported so /access/invitations (SCK-618) can reuse the same invite
 * dialog. SCK-619 upgrades the internal response handling; the public
 * shape stays: caller passes `mode="invite"`, `accountId`, `projects`,
 * `onClose`, `onSaved` — we handle the POST and call `onSaved` on success.
 */
export function MemberFormDialog({
  mode,
  member,
  projects,
  accountId,
  onClose,
  onSaved,
}) {
  const isEdit = mode === "edit";
  const [email, setEmail] = useState(member?.email || "");
  const [role, setRole] = useState(member?.role || "MEMBER");
  const [canCreate, setCanCreate] = useState(!!member?.canCreateProjects);
  const [grants, setGrants] = useState(() => {
    const seed = {};
    (member?.projects || []).forEach((p) => {
      seed[p.projectId] = p.role;
    });
    return seed;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // ACCOUNT_ADMIN implies canCreateProjects=true (server enforces too).
  useEffect(() => {
    if (role === "ACCOUNT_ADMIN") setCanCreate(true);
  }, [role]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit) {
        await updateMember(accountId, member.userId, {
          role,
          canCreateProjects: canCreate,
        });
        onSaved({ mode: "edit", email });
      } else {
        const projectGrants = Object.entries(grants)
          .filter(([, r]) => !!r)
          .map(([projectId, r]) => ({ projectId, role: r }));
        const response = await inviteMember(accountId, {
          email,
          role,
          canCreateProjects: canCreate,
          projectGrants,
        });
        // SCK-619 — coord SCK-617 wraps invite mints in
        // `{invitation, member}`. `invitation.redemptionUrl` +
        // `deliveryMode` drive whether we render a copy-link modal
        // (SES off, or deliveryMode === 'copy_only') or an
        // email-sent toast. Legacy shapes without `.invitation`
        // fall through with `null` and get the plain "invited" toast.
        const invitation =
          response && typeof response === "object" && response.invitation
            ? response.invitation
            : null;
        onSaved({ mode: "invite", email, invitation });
      }
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit member" : "Invite a member"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isEdit}
            required
          />
          <FormControl fullWidth>
            <InputLabel>Account role</InputLabel>
            <Select
              label="Account role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <MenuItem value="MEMBER">Member</MenuItem>
              <MenuItem value="ACCOUNT_ADMIN">Account Admin</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={canCreate}
                onChange={(e) => setCanCreate(e.target.checked)}
                disabled={role === "ACCOUNT_ADMIN"}
              />
            }
            label={
              role === "ACCOUNT_ADMIN"
                ? "Can create workspaces (admins always can)"
                : "Can create new workspaces"
            }
          />
          {!isEdit && projects.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Workspace access (optional)
              </Typography>
              <Stack spacing={1}>
                {projects.map((p) => (
                  <Box
                    key={p.id}
                    sx={{ display: "flex", alignItems: "center", gap: 2 }}
                  >
                    <Box sx={{ flex: 1 }}>{p.name}</Box>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select
                        displayEmpty
                        value={grants[p.id] || ""}
                        onChange={(e) =>
                          setGrants((g) => {
                            const next = { ...g };
                            if (e.target.value) next[p.id] = e.target.value;
                            else delete next[p.id];
                            return next;
                          })
                        }
                      >
                        <MenuItem value="">
                          <em>No access</em>
                        </MenuItem>
                        <MenuItem value="VIEWER">Viewer</MenuItem>
                        <MenuItem value="CONTRIBUTOR">Contributor</MenuItem>
                        <MenuItem value="PROJECT_ADMIN">Workspace Admin</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          {error && (
            <Typography sx={{ color: "error.main" }}>{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !email}
        >
          {submitting ? "Saving…" : isEdit ? "Save" : "Invite"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RemoveConfirmDialog({ member, accountId, onClose, onRemoved }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleRemove = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await removeMember(accountId, member.userId);
      onRemoved();
    } catch (err) {
      setError(err.message || "Remove failed");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Remove {member.email}?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2">
          They&apos;ll lose access to all workspaces in this account
          immediately. Their account remains; you can re-invite them later.
        </Typography>
        {error && (
          <Typography sx={{ color: "error.main", mt: 2 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleRemove}
          disabled={submitting}
        >
          {submitting ? "Removing…" : "Remove"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * SCK-714 — Reset NDA confirm dialog for the AccessMembers surface.
 *
 * Same shape as DirectoryUsers.ResetNdaConfirmDialog (parent-owns-toast
 * pattern): local `submitting` + inline error, MUI xs Dialog, target
 * email lifted into the DialogTitle so a mis-click on a same-named row
 * is catchable at a glance. Parent renders the success/failure toast
 * via `onDone({ severity, message })`.
 */
function ResetNdaConfirmDialogAM({ member, onClose, onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const label = member?.email || member?.username || `user ${member?.userId}`;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { message } = await resetUserNda(member.userId, member.email);
      onDone({ severity: "success", message });
    } catch (err) {
      setError(err?.message || "Reset failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Reset NDA for {label}?</DialogTitle>
      <DialogContent dividers>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, mb: 1 }}
          data-testid="reset-nda-target-email"
        >
          {label}
        </Typography>
        <Typography variant="body2">
          This user will be prompted to re-accept the NDA on their next
          login. Their account membership, roles, and project access are
          unchanged. This action is logged.
        </Typography>
        {error && (
          <Typography sx={{ color: "error.main", mt: 2 }} variant="body2">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting}
          data-testid="reset-nda-confirm"
        >
          {submitting ? "Resetting…" : "Reset NDA"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
